<template>
  <AuthLayout
    title="Reset Your Password"
    description="Create a new password for your account."
  >
    <div>
      <!-- Loading State (when verifying token) -->
      <div v-if="verifyingToken" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        <p class="mt-4 text-sm text-gray-600">Verifying reset link...</p>
      </div>

      <!-- Token Error (invalid/expired/used) -->
      <div v-if="tokenError && !verifyingToken">
        <div class="flex p-4 mb-4 bg-red-50 border border-red-200 rounded-lg">
          <div class="flex-shrink-0">
            <OctagonExclamationIcon class="w-5 h-5 text-red-400" />
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Invalid or Expired Link</h3>
            <div class="mt-2 text-sm text-red-700">
              <p>The reset password link is invalid or has expired.</p>
            </div>
          </div>
        </div>
        <div class="mt-6 flex gap-4">
          <BaseButton
            text="Request New Reset Link"
            variant="primary"
            size="sm"
            @click="$router.push('/forgot-password')"
          />
          <BaseButton
            text="Go to Login"
            variant="secondary"
            size="sm"
            class="ml-3"
            @click="$router.push('/')"
          />
        </div>
      </div>

      <!-- Success Message (after reset) -->
      <div v-if="success && !verifyingToken" class="p-6 mb-6 bg-green-50 border border-green-200 rounded-lg">
        <div class="flex">
          <div class="flex-shrink-0">
            <CheckIcon class="w-6 h-6 text-green-400" />
          </div>
          <div class="ml-4">
            <h3 class="text-sm font-medium text-green-800">Password Reset Successfully!</h3>
            <div class="mt-2 text-sm text-green-700">
              <p>Your password has been reset successfully. You can now log in to your account.</p>
            </div>
            <div class="mt-6">
              <BaseButton
                text="Go to Login"
                variant="primary"
                size="sm"
                @click="$router.push('/')"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Error Message (reset failed) -->
      <div v-if="error && !verifyingToken && !tokenError" class="p-6 mb-6 bg-red-50 border border-red-200 rounded-lg">
        <div class="flex">
          <div class="flex-shrink-0">
            <OctagonExclamationIcon class="w-6 h-6 text-red-400" />
          </div>
          <div class="ml-4">
            <h3 class="text-sm font-medium text-red-800">Error Resetting Password</h3>
            <div class="mt-2 text-sm text-red-700">
              <p>{{ getErrorMessage(error) }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Reset Form (only show if token is valid and not success) -->
      <div v-if="!verifyingToken && !tokenError && !success">
        <div class="p-6 mb-6 bg-blue-50 border border-blue-200 rounded-lg">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <UserCircleIcon class="w-8 h-8 text-blue-400" />
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-blue-800">Reset password for:</p>
              <p class="text-lg font-semibold text-blue-900">{{ userEmail }}</p>
              <p v-if="tokenValidUntil" class="mt-1 text-xs text-blue-600">
                Link expires: {{ formatExpiry(tokenValidUntil) }}
              </p>
            </div>
          </div>
        </div>

        <form @submit.prevent="handleSubmit">
          <div class="space-y-6">
            <div>
              <TextInputDefault
                v-model="form.password"
                labelText="New Password"
                placeholder="Enter your new password"
                id="password"
                name="password"
                type="password"
                :icon="showPassword ? 'eye-slash' : 'eye'"
                @icon-click="showPassword = !showPassword"
                :asterisk="true"
                :errorMessage="errors.password"
                :required="true"
                @update:modelValue="clearError('password')"
              />
              <div class="mt-2 text-xs text-gray-500 space-y-1">
                <p>Password must:</p>
                <ul class="list-disc list-inside ml-2">
                  <li>Be at least 6 characters long</li>
                  <li>Include uppercase and lowercase letters</li>
                  <li>Include at least one number</li>
                </ul>
              </div>
            </div>

            <div>
              <TextInputDefault
                v-model="form.confirmPassword"
                labelText="Confirm New Password"
                placeholder="Confirm your new password"
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                @icon-click="showConfirmPassword = !showConfirmPassword"
                :asterisk="true"
                :errorMessage="errors.confirmPassword"
                :required="true"
                @update:modelValue="clearError('confirmPassword')"
              />
            </div>

            <BaseButton
              type="submit"
              :text="submitting ? 'Resetting Password...' : 'Reset Password'"
              variant="primary"
              size="md"
              class="w-full"
              :disabled="submitting"
              :loading="submitting"
            />
          </div>
        </form>
      </div>

      <!-- Help Links -->
      <div v-if="!verifyingToken && !tokenError" class="mt-8 pt-6 border-t border-gray-200 text-center text-sm">
        <p class="text-gray-600">
          Remember your password? 
          <router-link to="/" class="font-medium text-blue-600 hover:text-blue-500">
            Go to Login
          </router-link>
        </p>
        <p class="mt-2 text-gray-600">
          Need a new reset link? 
          <router-link to="/forgot-password" class="font-medium text-blue-600 hover:text-blue-500">
            Request another
          </router-link>
        </p>
      </div>
    </div>
  </AuthLayout>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AuthLayout from '@/components/layout/AuthLayout.vue';
import TextInputDefault from '@/components/forms/TextInputDefault.vue';
import BaseButton from '@/components/Buttons/BaseButton.vue';
import { UserApiService } from '@/services/userApiService';
import {
  UserCircleIcon,
  CheckIcon,
  OctagonExclamationIcon
} from '@/icons';
import dayjs from 'dayjs';

const route = useRoute();
const router = useRouter();

// Define error interface
interface ApiError {
  message?: string;
  [key: string]: unknown;
}

// Reactive state
const form = reactive({
  password: '',
  confirmPassword: ''
});

const errors = reactive({
  password: '',
  confirmPassword: ''
});

const showPassword = ref(false);
const showConfirmPassword = ref(false);
const verifyingToken = ref(true); // Start with token verification
const submitting = ref(false);
const success = ref(false);
const error = ref('');
const tokenError = ref('');
const userEmail = ref('');
const tokenValidUntil = ref<Date | null>(null);
const token = ref('');

// Format expiry date
const formatExpiry = (date: Date): string => {
  return dayjs(date).format('MMM D, YYYY h:mm A');
};

// Helper function to extract and clean error message
const getErrorMessage = (errorMessage: unknown): string => {
  if (typeof errorMessage === 'string') {
    // Trim and clean common error messages
    const trimmed = errorMessage.trim();
    
    // Remove trailing punctuation if needed
    return trimmed.replace(/[.!]+$/, '');
  }
  
  if (errorMessage instanceof Error) {
    return errorMessage.message;
  }
  
  if (typeof errorMessage === 'object' && errorMessage !== null) {
    const errorObj = errorMessage as ApiError;
    if (errorObj.message) {
      return String(errorObj.message);
    }
  }
  
  return 'An unknown error occurred';
};

// Validate password strength
const validatePassword = (password: string): { isValid: boolean; message: string } => {
  if (password.length < 6) {
    return { isValid: false, message: 'Password must be at least 6 characters long' };
  }
  
  // Check for uppercase
  if (!/[A-Z]/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one uppercase letter' };
  }
  
  // Check for lowercase
  if (!/[a-z]/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one lowercase letter' };
  }
  
  // Check for number
  if (!/\d/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one number' };
  }
  
  return { isValid: true, message: '' };
};

// Validate form
const validateForm = (): boolean => {
  let isValid = true;
  
  // Clear previous errors
  errors.password = '';
  errors.confirmPassword = '';
  error.value = '';

  // Validate password
  if (!form.password) {
    errors.password = 'Password is required';
    isValid = false;
  } else {
    const passwordValidation = validatePassword(form.password);
    if (!passwordValidation.isValid) {
      errors.password = passwordValidation.message;
      isValid = false;
    }
  }

  // Validate confirm password
  if (!form.confirmPassword) {
    errors.confirmPassword = 'Please confirm your password';
    isValid = false;
  } else if (form.password !== form.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match';
    isValid = false;
  }

  return isValid;
};

// Clear specific error
const clearError = (field: keyof typeof errors) => {
  errors[field] = '';
  error.value = '';
};

// Verify token
const verifyToken = async (token: string): Promise<boolean> => {
  try {
    const response = await UserApiService.verifyResetToken(token);
    
    if (response.success && response.isValid) {
      userEmail.value = response.data?.email || 'your account';
      return true;
    } else {
      tokenError.value = getErrorMessage(response.message || 'Invalid reset link');
      return false;
    }
  } catch (err: unknown) {
    tokenError.value = getErrorMessage(err);
    return false;
  }
};

// Handle form submission
const handleSubmit = async () => {
  if (!validateForm()) {
    return;
  }

  submitting.value = true;
  error.value = '';

  try {
    await UserApiService.resetPassword(token.value, form.password);
    success.value = true;
    
    // Clear form
    form.password = '';
    form.confirmPassword = '';
    
    // Auto-redirect to login after 5 seconds
    setTimeout(() => {
      router.push('/');
    }, 5000);
    
  } catch (err: unknown) {
    const errorMessage = getErrorMessage(err);
    error.value = errorMessage;
    
    // If token is invalid/expired/used, update token error
    if (errorMessage.toLowerCase().includes('already been used') || 
        errorMessage.toLowerCase().includes('invalidated') ||
        errorMessage.toLowerCase().includes('invalid') ||
        errorMessage.toLowerCase().includes('expired')) {
      tokenError.value = errorMessage;
    }
  } finally {
    submitting.value = false;
  }
};

// On component mount
onMounted(async () => {
  const tokenParam = route.query.token as string;
  
  if (!tokenParam) {
    tokenError.value = 'No reset token found. Please check your email link.';
    verifyingToken.value = false;
    return;
  }

  token.value = tokenParam;

  try {
    // Verify token before showing form
    const isValid = await verifyToken(token.value);
    
    if (!isValid) {
      // Token is invalid/expired/used
      verifyingToken.value = false;
      return;
    }
    
  } catch (err: unknown) {
    tokenError.value = getErrorMessage(err);
    console.error('Reset token error:', err);
  } finally {
    verifyingToken.value = false;
  }
});
</script>

<style scoped>
/* Add any custom styles here */
</style>